import { FC, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Badge } from 'antd'

import * as ROUTES from 'src/constants/routes'
import { useAppSelector } from 'src/store/hooks'
import { useIsOrganisation } from 'src/hooks/useIsOrganisation'

import HomeIcon from 'src/assets/icons/navbar/home.svg?react'
import HomeActiveIcon from 'src/assets/icons/navbar/home-active.svg?react'
import ChatIcon from 'src/assets/icons/navbar/chat.svg?react'
import ChatActiveIcon from 'src/assets/icons/navbar/chat-active.svg?react'
import ConnectionsIcon from 'src/assets/icons/navbar/connections.svg?react'
import ConnectionsActiveIcon from 'src/assets/icons/navbar/connections-active.svg?react'
import MapIcon from 'src/assets/icons/navbar/map.svg?react'
import MapActiveIcon from 'src/assets/icons/navbar/map-active.svg?react'
import EventsIcon from 'src/assets/icons/navbar/events.svg?react'
import EventsActiveIcon from 'src/assets/icons/navbar/events-active.svg?react'

import { IconWrapper, StyledBottomNavList, StyledNavItem, StyledNavList } from './styled'

interface NavItem {
  key: string
  title: string
  icon: React.ReactNode
  activeIcon: React.ReactNode
  badge?: number
}

export const Menu: FC = () => {
  const location = useLocation()
  const unreadCounts = useAppSelector((state) => state.messages.unreadCounts)
  const totalUnreadMessages = Object.values(unreadCounts).reduce((a, b) => a + b, 0)
  const isOrganization = useIsOrganisation()

  const getActiveKey = (): string => {
    const { pathname } = location
    if (pathname.startsWith(ROUTES.PRIVATE_CHAT)) return ROUTES.PRIVATE_CHAT
    if (pathname.startsWith(ROUTES.PRIVATE_CONNECTIONS)) return ROUTES.PRIVATE_CONNECTIONS
    if (pathname.startsWith(ROUTES.PRIVATE_MAP)) return ROUTES.PRIVATE_MAP
    if (pathname.startsWith(ROUTES.PRIVATE_CALENDAR)) return ROUTES.PRIVATE_CALENDAR
    if (pathname === ROUTES.PRIVATE_HOME) return ROUTES.PRIVATE_HOME
    return ''
  }

  const activeKey = getActiveKey()

  const navItems: NavItem[] = useMemo(() => {
    const allItems: NavItem[] = [
      {
        key: ROUTES.PRIVATE_HOME,
        title: 'Home',
        icon: <HomeIcon />,
        activeIcon: <HomeActiveIcon />,
      },
      {
        key: ROUTES.PRIVATE_CHAT,
        title: 'Chat',
        icon: <ChatIcon />,
        activeIcon: <ChatActiveIcon />,
        badge: totalUnreadMessages > 0 ? totalUnreadMessages : undefined,
      },
      {
        key: ROUTES.PRIVATE_CONNECTIONS,
        title: 'Connections',
        icon: <ConnectionsIcon />,
        activeIcon: <ConnectionsActiveIcon />,
      },
      {
        key: ROUTES.PRIVATE_MAP,
        title: 'Map',
        icon: <MapIcon />,
        activeIcon: <MapActiveIcon />,
      },
      {
        key: ROUTES.PRIVATE_CALENDAR,
        title: 'My events',
        icon: <EventsIcon />,
        activeIcon: <EventsActiveIcon />,
      },
    ]

    if (isOrganization) {
      const restrictedKeys = [ROUTES.PRIVATE_CHAT, ROUTES.PRIVATE_CONNECTIONS]
      return allItems.filter((item) => !restrictedKeys.includes(item.key))
    }

    return allItems
  }, [totalUnreadMessages, isOrganization])

  return (
    <>
      <StyledNavList role="navigation" aria-label="Main navigation">
        {navItems.map((item) => {
          const isActive = activeKey === item.key
          return (
            <StyledNavItem key={item.key} $active={isActive}>
              <Link to={item.key} aria-current={isActive ? 'page' : undefined}>
                <IconWrapper aria-hidden="true">
                  {isActive ? item.activeIcon : item.icon}
                </IconWrapper>
                {item.title}
                {item.badge != null && <Badge count={item.badge} style={{ marginLeft: 4 }} />}
              </Link>
            </StyledNavItem>
          )
        })}
      </StyledNavList>
      <StyledBottomNavList aria-label="Safety">
        <StyledNavItem>
          <Link to={ROUTES.CONTACT}>Report Suspicious Behaviour</Link>
        </StyledNavItem>
      </StyledBottomNavList>
    </>
  )
}
